/**
 * QCE (Qualitative Coding Engine) redux slice — the "Themes (Beta)" data layer.
 *
 * Uses RTK core (`createSlice` + `createAsyncThunk`) over the `qceService`
 * client, read with `useSelector` — the same shape as how the app integrates the
 * node backend. We deliberately do NOT use RTK Query here: this app pairs
 * react-redux v9 with @reduxjs/toolkit v1.9, and RTK Query's hooks require RTK
 * v2 with react-redux v9, so its hooks can't subscribe to the store (they warn
 * "middleware not added" even when it is). Core thunks/selectors are unaffected.
 */
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { AnyAction, PayloadAction, ThunkDispatch } from "@reduxjs/toolkit";

import type { CodeSelectionDetail } from "../../../Pages/Hub/coding/codeSelectionEvent";
import type { RootState } from "../rootReducer";
import type {
  Theme,
  ThemeWrite,
  ThemeCode,
  ClipAttach,
  Exemplar,
  ThemeDraft,
  HighlightCreate,
} from "../../../Pages/Project/Tabs/ThemesTab/types";

import { qceService } from "./qceService";

type Status = "idle" | "loading" | "succeeded" | "failed";

// Stable empty reference so selectors don't return a fresh [] each render.
const EMPTY_CODES: ThemeCode[] = [];

export type PendingSelectionAction = "add-to-theme" | "highlight";

export interface PendingSelection {
  detail: CodeSelectionDetail;
  action: PendingSelectionAction;
}

export interface QceState {
  themes: Theme[];
  themesStatus: Status;
  codesByTheme: Record<string, ThemeCode[]>;
  codesStatus: Record<string, Status>;
  storyCodes: Record<string, ThemeCode[]>;
  pendingSelection: PendingSelection | null;
  // Staleness guards: the requestId of the most recently *dispatched* read for
  // each key. Mutations (add/delete/move) fire several overlapping refetches;
  // under network latency an earlier (pre-mutation) response can resolve last
  // and clobber the store with stale data. We apply a fulfilled read only when
  // it is still the newest in flight, so the final state always reflects the
  // last dispatched fetch. (Works locally where responses resolve in order;
  // manifested on staging as "moved highlight not removed / duplicated".)
  themesReqId?: string;
  codesReqId: Record<string, string>;
  storyCodesReqId: Record<string, string>;
}

const initialState: QceState = {
  themes: [],
  themesStatus: "idle",
  codesByTheme: {},
  codesStatus: {},
  storyCodes: {},
  pendingSelection: null,
  codesReqId: {},
  storyCodesReqId: {},
};

const tokenOf = (getState: () => unknown) => (getState() as RootState).auth?.token;

// ----- Reads -----
export const fetchThemes = createAsyncThunk("qce/fetchThemes", (_: void, { getState }) =>
  qceService.listThemes(tokenOf(getState))
);

export const fetchThemeCodes = createAsyncThunk("qce/fetchThemeCodes", (themeId: string, { getState }) =>
  qceService.listThemeCodes(tokenOf(getState), themeId)
);

export const fetchStoryCodes = createAsyncThunk("qce/fetchStoryCodes", (storyId: string, { getState }) =>
  qceService.listStoryCodes(tokenOf(getState), storyId)
);

// ----- Theme mutations (refetch themes for fresh counts) -----
export const createTheme = createAsyncThunk("qce/createTheme", async (body: ThemeWrite, { getState, dispatch }) => {
  const t = await qceService.createTheme(tokenOf(getState), body);
  dispatch(fetchThemes());
  return t;
});

export const updateTheme = createAsyncThunk(
  "qce/updateTheme",
  async ({ id, body }: { id: string; body: ThemeWrite }, { getState, dispatch }) => {
    const t = await qceService.updateTheme(tokenOf(getState), id, body);
    dispatch(fetchThemes());
    return t;
  }
);

export const deleteTheme = createAsyncThunk("qce/deleteTheme", async (id: string, { getState, dispatch }) => {
  const r = await qceService.deleteTheme(tokenOf(getState), id);
  dispatch(fetchThemes());
  return r;
});

export const generateThemeFromExamples = createAsyncThunk(
  "qce/generateTheme",
  (exemplars: Exemplar[], { getState }): Promise<ThemeDraft> =>
    qceService.generateThemeFromExamples(tokenOf(getState), exemplars)
);

export const attachClips = createAsyncThunk(
  "qce/attachClips",
  async ({ themeId, clips }: { themeId: string; clips: ClipAttach[] }, { getState, dispatch }) => {
    const r = await qceService.attachClips(tokenOf(getState), themeId, clips);
    dispatch(fetchThemes());
    dispatch(fetchThemeCodes(themeId));
    return r;
  }
);

// ----- Code mutations -----
export const updateCode = createAsyncThunk(
  "qce/updateCode",
  async (
    { codeId, patch, themeId }: { codeId: string; patch: { memo?: string; rating?: number | null }; themeId?: string },
    { getState, dispatch }
  ) => {
    const r = await qceService.updateCode(tokenOf(getState), codeId, patch);
    if (themeId) dispatch(fetchThemeCodes(themeId));
    return r;
  }
);

export const deleteCode = createAsyncThunk(
  "qce/deleteCode",
  async ({ codeId, themeId }: { codeId: string; themeId?: string }, { getState, dispatch }) => {
    const r = await qceService.deleteCode(tokenOf(getState), codeId);
    dispatch(fetchThemes());
    if (themeId) dispatch(fetchThemeCodes(themeId));
    return r;
  }
);

export const attachHighlight = createAsyncThunk(
  "qce/attachHighlight",
  async (body: HighlightCreate, { getState, dispatch }) => {
    const r = await qceService.attachHighlight(tokenOf(getState), body);
    dispatch(fetchThemes());
    return r;
  }
);

export const updateExcerptNote = createAsyncThunk(
  "qce/updateExcerptNote",
  ({ excerptId, note }: { excerptId: string; note: string }, { getState }) =>
    qceService.updateExcerptNote(tokenOf(getState), excerptId, note)
);

export const addExcerptTheme = createAsyncThunk(
  "qce/addExcerptTheme",
  async ({ excerptId, themeId }: { excerptId: string; themeId: string }, { getState, dispatch }) => {
    const r = await qceService.addExcerptTheme(tokenOf(getState), excerptId, themeId);
    dispatch(fetchThemes());
    return r;
  }
);

const qceSlice = createSlice({
  name: "qce",
  initialState,
  reducers: {
    setPendingSelection(s, a: PayloadAction<PendingSelection>) {
      s.pendingSelection = a.payload;
    },
    clearPendingSelection(s) {
      s.pendingSelection = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchThemes.pending, (s, a) => {
        s.themesStatus = "loading";
        s.themesReqId = a.meta.requestId;
      })
      .addCase(fetchThemes.fulfilled, (s, a) => {
        if (a.meta.requestId !== s.themesReqId) return; // superseded by a newer fetch
        s.themesStatus = "succeeded";
        s.themes = a.payload || [];
      })
      .addCase(fetchThemes.rejected, (s, a) => {
        if (a.meta.requestId !== s.themesReqId) return;
        s.themesStatus = "failed";
      })
      .addCase(fetchThemeCodes.pending, (s, a) => {
        s.codesStatus[a.meta.arg] = "loading";
        s.codesReqId[a.meta.arg] = a.meta.requestId;
      })
      .addCase(fetchThemeCodes.fulfilled, (s, a) => {
        if (a.meta.requestId !== s.codesReqId[a.meta.arg]) return; // superseded
        s.codesStatus[a.meta.arg] = "succeeded";
        s.codesByTheme[a.meta.arg] = a.payload || [];
      })
      .addCase(fetchThemeCodes.rejected, (s, a) => {
        if (a.meta.requestId !== s.codesReqId[a.meta.arg]) return;
        s.codesStatus[a.meta.arg] = "failed";
      })
      .addCase(fetchStoryCodes.pending, (s, a) => {
        s.storyCodesReqId[a.meta.arg] = a.meta.requestId;
      })
      .addCase(fetchStoryCodes.fulfilled, (s, a) => {
        if (a.meta.requestId !== s.storyCodesReqId[a.meta.arg]) return; // superseded
        s.storyCodes[a.meta.arg] = a.payload || [];
      });
  },
});

export const { setPendingSelection, clearPendingSelection } = qceSlice.actions;

// ----- Selectors -----
const selectThemes = (s: RootState) => s.qce.themes;
const selectThemesStatus = (s: RootState) => s.qce.themesStatus;

// ----- Typed dispatch + convenience hooks -----
type QceDispatch = ThunkDispatch<RootState, unknown, AnyAction>;

export const useQceDispatch = () => useDispatch<QceDispatch>();

/** Themes list — fetches once, then serves from the store. */
export function useThemes() {
  const dispatch = useQceDispatch();
  const themes = useSelector(selectThemes);
  const status = useSelector(selectThemesStatus);
  useEffect(() => {
    if (status === "idle") dispatch(fetchThemes());
  }, [status, dispatch]);
  return {
    themes,
    loading: status === "loading" || status === "idle",
    error: status === "failed",
    refetch: () => dispatch(fetchThemes()),
  };
}

/** A theme's codes — refetches when the theme id changes. */
export function useThemeCodes(themeId: string) {
  const dispatch = useQceDispatch();
  const codes = useSelector((s: RootState) => s.qce.codesByTheme[themeId]);
  const status = useSelector((s: RootState) => s.qce.codesStatus[themeId]);
  useEffect(() => {
    dispatch(fetchThemeCodes(themeId));
  }, [themeId, dispatch]);
  return {
    codes: codes || EMPTY_CODES,
    loading: (status === "loading" || status === undefined) && !codes,
    error: status === "failed",
  };
}

/** A story's codes for the Hub overlay — fetches when active. */
export function useStoryCodes(storyId: string | undefined, active: boolean) {
  const dispatch = useQceDispatch();
  const codes = useSelector((s: RootState) => (storyId ? s.qce.storyCodes[storyId] : undefined));
  useEffect(() => {
    if (active && storyId) dispatch(fetchStoryCodes(storyId));
  }, [active, storyId, dispatch]);
  return active && storyId ? codes || EMPTY_CODES : EMPTY_CODES;
}

export default qceSlice.reducer;
